import type { StorefrontAdapter, StorefrontCartItem } from '../types';
import { loadScriptOnce } from '../types';

declare global {
  interface Window {
    klaviyo?: any[] & {
      identify?: (p: Record<string, unknown>) => void;
      track?: (name: string, props: Record<string, unknown>) => void;
      trackViewedItem?: (props: Record<string, unknown>) => void;
      push?: (args: unknown[]) => void;
    };
    _learnq?: unknown[];
  }
}

/** Klaviyo's recommended pre-load queue: calls buffer until klaviyo.js arrives. */
function queue(): any {
  if (typeof window === 'undefined') return null;
  if (!window.klaviyo) {
    const k: any = [];
    // Each method buffers `[method, ...args]`; klaviyo.js drains the array on
    // load. Use the prototype push so the wrappers never shadow Array#push.
    for (const method of ['identify', 'track', 'trackViewedItem', 'openForm', 'isIdentified', 'account', 'cookieDomain']) {
      k[method] = (...args: unknown[]) => Array.prototype.push.call(k, [method, ...args]);
    }
    window.klaviyo = k;
  }
  return window.klaviyo;
}

function money(n: unknown): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
}

function cartProps(cart: StorefrontCartItem[]) {
  const items = cart.map((i) => ({
    ProductID: i.product_id,
    VariantID: i.variant_id,
    SKU: i.sku ?? undefined,
    ProductName: i.product_name,
    VariantName: i.variant_name ?? undefined,
    Quantity: i.quantity,
    ItemPrice: money(i.price),
    RowTotal: money(i.price * i.quantity),
    ImageURL: i.image_url ?? undefined,
  }));
  return {
    $value: money(cart.reduce((s, i) => s + i.price * i.quantity, 0)),
    ItemNames: cart.map((i) => i.product_name),
    Items: items,
    CheckoutURL: typeof window !== 'undefined' ? `${window.location.origin}/checkout` : undefined,
  };
}

export const klaviyoAdapter: StorefrontAdapter = {
  id: 'klaviyo',
  isConfigured: (cfg) => typeof cfg.company_id === 'string' && /^[A-Za-z0-9]{5,10}$/.test(cfg.company_id),
  load(cfg) {
    queue();
    loadScriptOnce('klaviyo-onsite', `https://static.klaviyo.com/onsite/js/${encodeURIComponent(cfg.company_id)}/klaviyo.js`);
  },
  identify(user) {
    const k = queue();
    if (!k || !user.email) return;
    const props: Record<string, unknown> = { email: user.email };
    if (user.first_name) props.first_name = user.first_name;
    if (user.last_name) props.last_name = user.last_name;
    if (user.id) props.external_id = user.id;
    k.identify(props);
  },
  viewedProduct(p) {
    const k = queue();
    if (!k) return;
    const url = p.url ?? (typeof window !== 'undefined' ? window.location.href : undefined);
    const props = {
      ProductName: p.name,
      ProductID: p.id,
      SKU: p.sku ?? undefined,
      Categories: p.categories ?? [],
      ImageURL: p.image_url ?? undefined,
      URL: url,
      Price: money(p.price),
      CompareAtPrice: p.compare_at_price != null ? money(p.compare_at_price) : undefined,
    };
    k.track('Viewed Product', props);
    k.trackViewedItem({
      Title: p.name,
      ItemId: p.id,
      Categories: p.categories ?? [],
      ImageUrl: p.image_url ?? undefined,
      Url: url,
      Metadata: { Price: money(p.price), CompareAtPrice: props.CompareAtPrice },
    });
  },
  addedToCart(item, cart) {
    const k = queue();
    if (!k) return;
    k.track('Added to Cart', {
      ...cartProps(cart),
      AddedItemProductName: item.product_name,
      AddedItemProductID: item.product_id,
      AddedItemVariantID: item.variant_id,
      AddedItemSKU: item.sku ?? undefined,
      AddedItemImageURL: item.image_url ?? undefined,
      AddedItemPrice: money(item.price),
      AddedItemQuantity: item.quantity,
    });
  },
  startedCheckout(cart, total) {
    const k = queue();
    if (!k || cart.length === 0) return;
    k.track('Started Checkout', { ...cartProps(cart), $value: money(total), $event_id: `checkout:${Date.now()}` });
  },
  // Placed Order is sent server-side (dedupes on order id); nothing client-side.
  orderCompleted() {},
};
